// Floating reactions: emoji drift up and fade over the video they belong to.
// Each reaction carries the identity of the camera it was aimed at, so it
// animates on that tile rather than over the whole stage; anything without a
// matching tile falls back to a stage-wide layer.

// Must match the worker's allowlist in worker/src/reactions.ts.
export const REACTIONS = ['❤️', '🔥', '👏', '😂', '😮', '👍', '👎', '💩', '😡']

// Cap what's on screen: a spamming viewer should never bury the video.
const MAX_PER_LAYER = 24

const layers = new WeakMap<HTMLElement, HTMLElement>()

function layerFor(host: HTMLElement): HTMLElement {
  let layer = layers.get(host)
  if (layer?.isConnected) return layer
  layer = document.createElement('div')
  layer.className = 'react-layer'
  host.append(layer)
  layers.set(host, layer)
  return layer
}

function spawnInto(layer: HTMLElement, emoji: string, delay: number): void {
  if (layer.childElementCount > MAX_PER_LAYER) return
  const node = document.createElement('span')
  node.className = 'react-float'
  node.textContent = emoji
  // Rise most of the way up whatever it's floating over: a percentage would
  // be relative to the emoji itself, and a vh unit ignores the tile entirely.
  const travel = Math.max(120, Math.round((layer.clientHeight || 240) * 0.82))
  node.style.setProperty('--rise', `${travel}px`)
  // Scatter start point and drift so a burst isn't a single column.
  node.style.left = `${8 + Math.random() * 30}%`
  node.style.setProperty('--drift', `${(Math.random() * 2 - 1) * 40}px`)
  node.style.setProperty('--spin', `${(Math.random() * 2 - 1) * 24}deg`)
  node.style.fontSize = `${18 + Math.random() * 12}px`
  node.style.animationDelay = `${delay}ms`
  node.addEventListener('animationend', () => node.remove())
  layer.append(node)
}

export interface ReactionLayer {
  // `target` is the element the reaction belongs to (a video tile); without
  // one it plays over the stage.
  spawn(emoji: string, count?: number, target?: HTMLElement | null): void
  destroy(): void
}

export function mountReactions(stage: HTMLElement): ReactionLayer {
  const stageLayer = layerFor(stage)
  return {
    spawn(emoji, count = 1, target) {
      const layer = target?.isConnected ? layerFor(target) : stageLayer
      for (let i = 0; i < Math.min(10, count); i++) spawnInto(layer, emoji, i * 90)
    },
    destroy() {
      stageLayer.remove()
    },
  }
}

// The clickable bar. `send` fires once per tap; the caller decides whether
// that means an HTTP post or a data message on the room.
export function reactionBar(send: (emoji: string) => void): HTMLElement {
  const bar = document.createElement('div')
  bar.className = 'react-bar'
  for (const emoji of REACTIONS) {
    const btn = document.createElement('button')
    btn.className = 'react-btn'
    btn.type = 'button'
    btn.textContent = emoji
    btn.title = `React ${emoji}`
    btn.onclick = () => {
      send(emoji)
      btn.classList.remove('pop')
      void btn.offsetWidth // restart the animation
      btn.classList.add('pop')
    }
    bar.append(btn)
  }
  return bar
}

// Batches taps so a rapid-fire viewer sends a few requests, not dozens.
export function makeSender(
  post: (emoji: string, count: number) => void,
  windowMs = 500,
): (emoji: string) => void {
  const pending = new Map<string, number>()
  let timer: ReturnType<typeof setTimeout> | null = null
  return (emoji: string) => {
    pending.set(emoji, (pending.get(emoji) ?? 0) + 1)
    if (timer) return
    timer = setTimeout(() => {
      for (const [e, n] of pending) post(e, n)
      pending.clear()
      timer = null
    }, windowMs)
  }
}
